/* eslint-disable no-throw-literal */
/* eslint-disable react-hooks/exhaustive-deps */
import { useLocation } from "react-router-dom";
import { AppContext, AuthContext } from "../../../store";
import React from "react";
import { useImmer } from "use-immer";
import { useDropdownApis } from "../../../shared";
import qs from "qs";
import {
  downloadReport,
  getAccountList as getAccountListApi,
} from "../api/ListApis";
import fileDownload from "js-file-download";
import { arraySort, errorMessage } from "../../../utils";

const defaultFilter = {
  options: {
    stores: [],
    financialYears: [],
    accountMonths: [],
  },
  values: { store: [], financialYear: [], month: [] },
  appliedFilters: [],
};

export const useAccountUploads = () => {
  const location = useLocation();
  const isStoreAdmin = location.pathname.includes("/store-admin");
  const { auth } = React.useContext(AuthContext);
  const { appState } = React.useContext(AppContext);
  const { globalData } = appState;

  const [state, setState] = useImmer({
    tableLoading: true,
    accountUploadList: [],
    searchText: "",
    filter: {
      options: {
        stores: [],
        financialYears: [],
        accountMonths: [],
      },
      values: { store: [], financialYear: [], month: [] },
      appliedFilters: [],
      isFilterApplied: true,
    },
  });

  const { franchiseeWiseStoreList } = useDropdownApis({
    isFranchiseeWiseStoreList: !isStoreAdmin,
  });

  // ================================================= API SECTION ===========================================
  const getAccountList = async ({ searchText = "", filters = null }) => {
    triggerTableLoading(true);
    let params = {};
    try {
      if (filters) {
        const appliedFilters = await getFilterParams(filters);
        params = { ...appliedFilters };
      }

      if (searchText) {
        params.search = searchText;
      }

      if (isStoreAdmin && auth?.data?.selectedStoreId) {
        params.store_id = [auth.data.selectedStoreId];
      }

      const query = {
        params,
        paramsSerializer: (params) => qs.stringify(params, { encode: true }),
      };

      const response = await getAccountListApi(query);
      const { success, message, data } = response;

      if (success) {
        setState((draft) => {
          draft.accountUploadList = data;
          draft.filter.isFilterApplied = true;
        });
      } else {
        throw {
          response: {
            data: { message },
          },
        };
      }
    } catch (err) {
      const message =
        err.response.data.message ?? `Failed to fetch account uploads list`;
      errorMessage(message);
    } finally {
      triggerTableLoading(false);
    }
  };

  const downloadUploadedFile = async (id) => {
    try {
      const res = await downloadReport(id);
      const content = res.headers["content-type"];
      fileDownload(res.data, `PL&BS_upload_file.xlsx`, content);
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };

  // ================================================= UTIL FUNCTION SECTION ===========================================

  const triggerTableLoading = (loading) => {
    setState((draft) => {
      draft.tableLoading = loading;
    });
  };

  const handleSearch = async (searchText) => {
    setState((draft) => {
      draft.searchText = searchText;
    });

    await getAccountList({
      searchText,
      filters: state.filter.values,
    });
  };

  const convertFilterToAppliedFilter = (appliedFilter) =>
    Object.entries(appliedFilter).flatMap(([key, value]) =>
      value.map((item) => ({
        type: key.replace(key[0], key[0].toUpperCase()),
        label: item.label,
        accessorKey: key,
      }))
    );

  const handleChangeFilter = ({ name, value }) => {
    setState((draft) => {
      draft.filter.values[name] = value;
      draft.filter.isFilterApplied = false;
    });
  };

  const getAppliedFilter = (appliedFilters) => {
    setState((draft) => {
      draft.filter.appliedFilters = appliedFilters;
    });
  };

  const getFilterParams = async (filterValues) => {
    const params = {};
    Object.entries(filterValues).forEach(([key, filter]) => {
      const actualKey =
        key === "store" ? "store_id" : key === "financialYear" ? "year" : key;
      filter.length > 0 &&
        (params[actualKey] = filter.map(({ value }) => value));
    });

    return params;
  };

  const handleApplyFilter = async (filters) => {
    await getAccountList({
      searchText: state.searchText,
      filters,
    });
  };

  const handleClearAllFilter = async () => {
    setState((draft) => {
      draft.filter.appliedFilters = [];
      draft.filter.values = defaultFilter.values;
    });

    await handleApplyFilter({});
  };

  const handleClearFilter = ({ accessorKey, label }) => {
    handleChangeFilter({
      name: accessorKey,
      value: state.filter?.values[accessorKey].filter(
        (item) => item?.label !== label
      ),
    });
  };

  // ================================================= USE EFFECT SECTION ===========================================
  React.useState(() => {
    getAccountList({
      filters: state.filter.values,
      searchText: state.searchText,
    });
  }, []);

  React.useEffect(() => {
    setState((draft) => {
      const accountMonthList = globalData?.pl_bs_reports?.pl_bs_accounts_months;
      const financialYearList = globalData?.pl_bs_reports?.pl_bs_accounts_years;

      if (accountMonthList) {
        const months = accountMonthList.map((month) => {
          const updatedMonth = {
            ...month,

            quarter:
              month.value <= 3
                ? "Q1"
                : month.value <= 6
                ? "Q2"
                : month.value <= 9
                ? "Q3"
                : "Q4",
          };

          return updatedMonth;
        });

        draft.filter.options.accountMonths = months;
      }
      if (financialYearList) {
        const years = financialYearList;
        draft.filter.options.financialYears = years;
      }
    });
  }, [globalData]);

  React.useEffect(() => {
    if (franchiseeWiseStoreList && !isStoreAdmin) {
      const stores = [
        ...franchiseeWiseStoreList.map((store, index) => ({
          label: store.name,
          value: store.id,
          franchisee_id: store.franchisee_id,
          franchisee_name: store.franchisee_name,
        })),
      ];
      setState((draft) => {
        if (franchiseeWiseStoreList.length > 0) {
          draft.filter.options.stores = arraySort(stores, "franchisee_name");
        }
      });
    }
  }, [franchiseeWiseStoreList]);

  React.useEffect(() => {
    getAppliedFilter(convertFilterToAppliedFilter(state.filter.values));
  }, [state.filter.values]);

  return {
    state,
    handleSearch,
    handleClearFilter,
    handleClearAllFilter,
    handleApplyFilter,
    handleChangeFilter,
    downloadUploadedFile,
  };
};
